import { useScopeAssignments } from '@/features/compliance/assignments/scope-api';
import { useAssignments } from '@/features/compliance/assignments/api';
import { AssigneeCell } from './AssigneeCell';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { InheritanceTier } from '@grc/shared';

interface ScopeInheritanceChainProps {
  frameworkId: string;
  controlId: string;
  controlCategory: string | null;
  effectiveTier: InheritanceTier;
}

export function ScopeInheritanceChain({
  frameworkId,
  controlId,
  controlCategory,
  effectiveTier,
}: ScopeInheritanceChainProps) {
  const { data: scopeRows, isLoading: scopeLoading } = useScopeAssignments(frameworkId);
  const { data: l3Assignments, isLoading: l3Loading } = useAssignments(controlId);

  if (scopeLoading || l3Loading) return <Skeleton className="h-24 w-full" />;

  const scopeList = scopeRows ?? [];
  const l3List = l3Assignments ?? [];

  // L1: framework-wide (category IS NULL)
  const l1 = scopeList.find((s) => s.category === null && s.is_active && !s.deleted_at) ?? null;
  // L2: category-level matching the control's category
  const l2 = controlCategory
    ? (scopeList.find((s) => s.category === controlCategory && s.is_active && !s.deleted_at) ?? null)
    : null;
  // L3: per-control assignment (first active)
  const l3 = l3List[0] ?? null;

  const rows: Array<{
    tier: InheritanceTier;
    label: string;
    scope: string;
    displayName: string | null | undefined;
    email: string | null | undefined;
    isPending: boolean | undefined;
    isActive: boolean;
  }> = [
    {
      tier: 'control',
      label: 'L3',
      scope: 'Control-specific',
      displayName: l3 ? (l3.assignee_display_name ?? null) : null,
      email: l3 ? (l3.assignee_email ?? null) : null,
      isPending: l3 ? (l3.assignee_is_pending ?? false) : false,
      isActive: !!l3,
    },
    {
      tier: 'category',
      label: 'L2',
      scope: controlCategory ? `Category: ${controlCategory}` : 'No category',
      displayName: l2?.assignee_display_name,
      email: l2?.assignee_email,
      isPending: l2?.assignee_is_pending,
      isActive: !!l2,
    },
    {
      tier: 'framework',
      label: 'L1',
      scope: 'Framework-wide',
      displayName: l1?.assignee_display_name,
      email: l1?.assignee_email,
      isPending: l1?.assignee_is_pending,
      isActive: !!l1,
    },
  ];

  return (
    <div className="space-y-2">
      {rows.map((row) => {
        const isEffective = row.tier === effectiveTier;
        return (
          <div
            key={row.tier}
            className={cn(
              'flex items-center gap-3 rounded-md border px-3 py-2.5 transition-colors',
              isEffective
                ? 'border-accent-sky/40 bg-accent-sky/5'
                : 'border-border-subtle bg-bg-surface opacity-60',
            )}
          >
            <Badge
              variant={isEffective ? 'sky' : 'muted'}
              className="text-[9px] shrink-0 w-6 justify-center"
            >
              {row.label}
            </Badge>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-text-secondary">{row.scope}</p>
            </div>
            <div className="shrink-0">
              {row.isActive ? (
                <AssigneeCell
                  displayName={row.displayName}
                  email={row.email}
                  isPending={row.isPending}
                />
              ) : (
                <span className="text-xs text-text-secondary italic">Unassigned</span>
              )}
            </div>
            {isEffective && (
              <Badge variant="emerald" className="text-[9px] shrink-0">Active</Badge>
            )}
          </div>
        );
      })}
    </div>
  );
}
